import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import PaymentService from '../../../services/paymentService';

// Mobile-tech payment collection screen — surfaces after Complete Service on the
// last service of a retail stop. Mandatory: tech MUST pick one of the 4 chips
// (no Skip) before the stop can close.
//
// Mirrors the web payment-method flow in requestimates.jsx (handlePayment + the
// materialize / handleSettle / settleOffline trio) but condensed for in-field use.
//
// Props:
//   show              — boolean, controls visibility
//   invoice           — { invoiceId, paymentId, amount, draft, requestId, requestType }
//                       — caller is responsible for creating the invoice (via
//                       PaymentService.createInvoice) before mounting this screen.
//   requestId         — request the invoice belongs to (drives state transitions).
//   installerId       — tech / installer recording the payment.
//   customerName      — string, displayed in the prompt
//   vehicleLabel      — string, displayed under customer name
//   onCollected(result) — fires after a chip's action completes. result shape:
//                          { method: 'card_now' | 'card_link' | 'cash' | 'check',
//                            checkNumber?: string,
//                            hostedUrl?: string,
//                            invoiceId?, offline?: boolean }
//                       Pay now opens the hosted URL via window.open before
//                       firing this callback so the caller can dismiss the screen.

const formatMoney = (cents) => {
  if (cents == null) return '—';
  const n = typeof cents === 'string' ? parseFloat(cents) : cents;
  if (Number.isNaN(n)) return '—';
  // Server amounts come in cents; humanize to dollars.
  return `$${(n / 100).toFixed(2)}`;
};

const PaymentCollectionScreen = ({
  show,
  invoice,
  requestId,
  installerId,
  customerName,
  vehicleLabel,
  onCollected,
}) => {
  const [selectedChip, setSelectedChip] = useState(null);  // 'card_now' | 'card_link' | 'cash' | 'check' | null
  const [checkNumber, setCheckNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // The atomic complete endpoint emails the customer a hosted pay link, so by
  // the time the tech reaches this screen the customer may have already paid
  // on their own device. Check invoice.paid on mount and short-circuit if so.
  const [statusChecked, setStatusChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkAlreadyPaid = async () => {
      if (!show || statusChecked) return;
      // Only real (materialized) invoices can be pre-paid by the customer.
      // Drafts have no hosted page exposed yet.
      const realInvoiceId = invoice?.invoiceId;
      if (!realInvoiceId || invoice?.draft) {
        setStatusChecked(true);
        return;
      }
      try {
        const stripeInvoice = await PaymentService.fetchInvoice(realInvoiceId);
        if (cancelled) return;
        if (stripeInvoice?.paid || stripeInvoice?.status === 'paid') {
          onCollected && onCollected({
            method: 'already_paid',
            invoiceId: realInvoiceId,
          });
          return;
        }
      } catch (err) {
        // Soft-fail — if we can't check status, fall through to the chips.
        console.warn('PaymentCollectionScreen: pre-paid check failed', err?.message || err);
      } finally {
        if (!cancelled) setStatusChecked(true);
      }
    };
    checkAlreadyPaid();
    return () => { cancelled = true; };
  }, [show, invoice, statusChecked, onCollected]);

  const reset = () => {
    setSelectedChip(null);
    setCheckNumber('');
    setProcessing(false);
    setError(null);
    setStatusChecked(false);
  };

  // Single wrapper around the four PaymentService orchestrators. Maps the chip
  // identifier to the corresponding collectVia* call, then forwards the result
  // to the caller via onCollected. Errors stay on this screen so the tech can
  // retry or pick a different chip without re-opening the modal.
  const runCollection = async (method) => {
    if (!invoice) {
      setError('Invoice is missing. Try reopening the screen.');
      return;
    }
    setProcessing(true);
    setError(null);
    try {
      let result;
      if (method === 'card_now') {
        const { hostedUrl, invoiceId } = await PaymentService.collectViaCardNow(invoice);
        // Tech may hand phone to customer here, or enter card themselves on the
        // hosted page. window.open synchronously to avoid popup blocking.
        if (hostedUrl) window.open(hostedUrl, '_blank', 'noopener,noreferrer');
        result = { hostedUrl, invoiceId };
      } else if (method === 'card_link') {
        result = await PaymentService.collectViaCardLink(invoice);
      } else if (method === 'cash') {
        result = await PaymentService.collectViaCash(invoice, { requestId, installerId });
      } else if (method === 'check') {
        const trimmed = (checkNumber || '').trim() || null;
        result = await PaymentService.collectViaCheck(invoice, {
          requestId,
          installerId,
          checkNumber: trimmed,
        });
      }
      onCollected && onCollected({ method, ...(result || {}) });
      reset();
    } catch (err) {
      console.error(`PaymentCollectionScreen: ${method} failed`, err);
      setError(err?.message || 'Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayNow = () => runCollection('card_now');
  const handleSendLink = () => runCollection('card_link');
  const handleCashConfirm = () => runCollection('cash');
  const handleCheckConfirm = () => runCollection('check');

  const total = invoice ? formatMoney(invoice.amount) : '—';
  const invoiceLabel = invoice?.invoiceId
    ? `Invoice #${invoice.invoiceId}`
    : invoice?.draft
    ? 'Draft invoice'
    : 'New invoice';

  // No onHide — collection is mandatory. Stop cannot close until a chip fires.
  return (
    <Modal show={show} backdrop="static" keyboard={false} centered size="sm">
      <Modal.Body className="py-4 px-3">
        <div className="text-center mb-3">
          <div className="text-muted" style={{ fontSize: 12, letterSpacing: 1 }}>
            {invoiceLabel}
          </div>
          <div style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.2, marginTop: 4 }}>
            {total}
          </div>
          {customerName && (
            <div className="text-muted mt-2" style={{ fontSize: 14 }}>
              {customerName}
              {vehicleLabel ? ` · ${vehicleLabel}` : ''}
            </div>
          )}
        </div>

        <hr className="my-3" />

        {error && (
          <div
            className="alert alert-danger py-2 mb-3"
            style={{ fontSize: 13 }}
            role="alert"
          >
            {error}
          </div>
        )}

        {processing && (
          <div className="text-center mb-3">
            <Spinner animation="border" size="sm" /> <span className="ml-2">Working…</span>
          </div>
        )}

        {!statusChecked && !processing && (
          <div className="text-center mb-3 text-muted" style={{ fontSize: 13 }}>
            <Spinner animation="border" size="sm" /> <span className="ml-2">Checking payment status…</span>
          </div>
        )}

        {statusChecked && !processing && selectedChip === null && (
          <>
            <div className="text-muted mb-3 text-center" style={{ fontSize: 14 }}>
              How is {customerName ? customerName.split(' ')[0] : 'the customer'} paying?
            </div>

            <Button
              variant="outline-primary"
              size="lg"
              block
              className="mb-2 text-left"
              onClick={handlePayNow}
              disabled={!invoice}
            >
              <i className="fa-sharp fa-regular fa-credit-card mr-2" aria-hidden="true" />
              <strong>Pay now</strong>
              <div className="text-muted" style={{ fontSize: 12, marginLeft: 28 }}>
                Open invoice on this device
              </div>
            </Button>

            <Button
              variant="outline-primary"
              size="lg"
              block
              className="mb-2 text-left"
              onClick={handleSendLink}
              disabled={!invoice}
            >
              <i className="fa-sharp fa-regular fa-paper-plane mr-2" aria-hidden="true" />
              <strong>Send pay link</strong>
              <div className="text-muted" style={{ fontSize: 12, marginLeft: 28 }}>
                SMS + email to customer
              </div>
            </Button>

            <Button
              variant="outline-primary"
              size="lg"
              block
              className="mb-2 text-left"
              onClick={() => setSelectedChip('cash')}
              disabled={!invoice}
            >
              <i className="fa-sharp fa-regular fa-money-bill-1 mr-2" aria-hidden="true" />
              <strong>Cash collected</strong>
            </Button>

            <Button
              variant="outline-primary"
              size="lg"
              block
              className="mb-0 text-left"
              onClick={() => setSelectedChip('check')}
              disabled={!invoice}
            >
              <i className="fa-sharp fa-regular fa-money-check-pen mr-2" aria-hidden="true" />
              <strong>Check collected</strong>
            </Button>
          </>
        )}

        {statusChecked && !processing && selectedChip === 'cash' && (
          <>
            <div className="text-center mb-3" style={{ fontSize: 15 }}>
              Confirm <strong>{total}</strong> received in cash?
            </div>
            <Button variant="success" size="lg" block onClick={handleCashConfirm}>
              <i className="fa-sharp fa-regular fa-check mr-2" aria-hidden="true" />
              Yes, mark paid
            </Button>
            <Button variant="link" size="sm" block onClick={() => setSelectedChip(null)}>
              ← Back
            </Button>
          </>
        )}

        {statusChecked && !processing && selectedChip === 'check' && (
          <>
            <div className="text-center mb-3" style={{ fontSize: 15 }}>
              Confirm <strong>{total}</strong> received by check
            </div>
            <Form.Group>
              <Form.Label style={{ fontSize: 13 }}>Check number (optional)</Form.Label>
              <Form.Control
                type="text"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="e.g. 1024"
                autoFocus
              />
            </Form.Group>
            <Button variant="success" size="lg" block onClick={handleCheckConfirm}>
              <i className="fa-sharp fa-regular fa-check mr-2" aria-hidden="true" />
              Mark paid
            </Button>
            <Button variant="link" size="sm" block onClick={() => setSelectedChip(null)}>
              ← Back
            </Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PaymentCollectionScreen;
